import { Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { AssetLogListItem } from '@evimo/types/api/goldenRecord/find-asset-logs/find-asset-logs.response.dto';
import { AssetLogDirectionLabel } from '~/app/components/asset-detail/AssetLogDirectionLabel';
import { summarizeAssetLogContent } from '~/app/components/asset-detail/assetLogContent.utils';
import styles from '~/app/components/asset-detail/AssetLogsTable/AssetLogsTable.module.css';
import { formatLogTimestampParts } from '~/app/utils/format-log-timestamp';

const { Text } = Typography;

export function useAssetLogsTableColumns() {
  const { t, i18n } = useTranslation('assets');

  const columns: ColumnsType<AssetLogListItem> = [
    {
      title: t('columns.receivedAt'),
      key: 'receivedAt',
      width: 150,
      render: (_: unknown, log: AssetLogListItem) => {
        const dt = log.receivedAt ?? log.createdAt;
        const timestamp = dt instanceof Date ? dt.toISOString() : dt;
        const parts = formatLogTimestampParts(timestamp, i18n.language);

        if (!parts) {
          return <Text type="secondary">—</Text>;
        }

        return (
          <div className={styles.timestamp}>
            <span className={styles.timestampDate}>{parts.date}</span>
            <span className={styles.timestampTime}>{parts.time}</span>
          </div>
        );
      },
    },
    {
      title: t('columns.action', 'Action'),
      key: 'action',
      width: 160,
      render: (_: unknown, log: AssetLogListItem) => {
        const summary = summarizeAssetLogContent(log.rawLog);
        return summary.action ? (
          <span className={styles.actionText}>{summary.action}</span>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: t('columns.connectorId', 'Connector'),
      key: 'connectorId',
      width: 110,
      align: 'center',
      render: (_: unknown, log: AssetLogListItem) => {
        const summary = summarizeAssetLogContent(log.rawLog);
        return summary.connectorId !== null ? (
          <div className={styles.connectorCell}>
            <span className={styles.connectorText}>{summary.connectorId}</span>
          </div>
        ) : (
          <span className={styles.connectorEmpty}>—</span>
        );
      },
    },
    {
      title: t('columns.direction', 'Direction'),
      key: 'direction',
      width: 130,
      render: (_: unknown, log: AssetLogListItem) => {
        const summary = summarizeAssetLogContent(log.rawLog);
        return <AssetLogDirectionLabel direction={summary.direction} />;
      },
    },
    {
      title: t('columns.status'),
      key: 'isTreated',
      width: 130,
      render: (_: unknown, log: AssetLogListItem) =>
        log.isTreated ? (
          <span className={`${styles.statusPill} ${styles.statusTreated}`}>
            <span className={styles.statusDot} aria-hidden />
            {t('treated')}
          </span>
        ) : (
          <span className={`${styles.statusPill} ${styles.statusUntreated}`}>
            <span className={styles.statusDot} aria-hidden />
            {t('untreated')}
          </span>
        ),
    },
  ];

  return { columns };
}
